/**
 * プラン定義（制限値・機能フラグ・超過課金）。
 *
 * **上限・機能フラグ・超過単価はここが唯一の出典。** API 起動時に `plans`
 * テーブルへ upsert し、制限チェック（quota enforcement）は必ず DB の値ではなく
 * この型を通した `PlanLimits` を使って行う。
 *
 * **`monthlyPrice` / `yearlyPrice` / `isPublic` / `sortOrder` だけは別扱い。**
 * これらは「新規に plans 行を作るときの初期値」でしかなく、**正は DB の行**。
 * 事業判断で運営コンソール（`PATCH /ops/billing/plans/:code`）から変更する。
 * `seedPlans` は既存行の価格を上書きしない（上書きすると、決めた価格が
 * worker の再起動のたびに黙ってカタログ値へ戻る）。
 *
 * 価格はすべて**税抜**。総額表示は `taxIncludedAmount()` /
 * `formatJpyWithTax()`（`billing-math.ts`）を通す。
 *
 * バイト量は **number** で表す。JavaScript の安全整数上限は約 9 PiB なので
 * メールホスティングの容量表現には十分で、JSON をそのまま往復できる
 * （bigint だと JSON.stringify が throw する）。
 */
import { z } from 'zod';
import { DEFAULT_TAX_RATE_BP, SLA_UPTIME_GUARANTEE_BP, taxIncludedAmount } from './billing-math.js';

export const planLimitsSchema = z.object({
  /** 0 = 無制限 */
  domainsMax: z.number().int().nonnegative(),
  mailboxesMax: z.number().int().nonnegative(),
  aliasesMax: z.number().int().nonnegative(),
  forwardsPerMailboxMax: z.number().int().nonnegative(),
  /** 契約全体の合計容量（バイト） */
  storageBytesTotal: z.number().int().nonnegative(),
  /** メールボックス個別の既定クォータ（バイト） */
  mailboxQuotaBytesDefault: z.number().int().nonnegative(),
  mailboxQuotaBytesMax: z.number().int().nonnegative(),
  /** Postfix message_size_limit に反映 */
  messageSizeBytesMax: z.number().int().positive(),
  /** 乗っ取り検知の閾値。Rspamd ratelimit と送信ポリシーに反映 */
  sendPerHourMax: z.number().int().positive(),
  sendPerDayMax: z.number().int().positive(),
  recipientsPerMessageMax: z.number().int().positive(),
  imapConnectionsPerUserMax: z.number().int().positive(),
  /** ゴミ箱の自動削除日数（0 = 自動削除しない） */
  retentionDaysTrash: z.number().int().nonnegative(),
  /**
   * 配信コンテンツの画像・添付の合計容量（バイト、0 = 無制限）。
   * `storageBytesTotal`（メールボックスの容量）とは**別枠**にする。
   * 同じ枠にすると「バナー画像を置いたせいで受信できなくなった」が起きる。
   *
   * `.default()` を付けてあるのは、この列が入る前に `plans` へ書かれた
   * 既存行を `planLimitsSchema.parse()` が落とさないようにするため
   * （API 起動時の再 upsert より前に読まれる経路がある）。
   */
  contentAssetBytes: z.number().int().nonnegative().default(0),
  /** 公開申込フォームの作成上限（0 = 無制限）。list-bombing の被害面積を絞る。 */
  signupFormsMax: z.number().int().nonnegative().default(0),
  /**
   * そのプランで保証する月間稼働率（basis point。99.5% = 9950）。**0 = SLA 無し**。
   *
   * **SLA クレジットの階梯をプラン別に持つための唯一の入力**（R21）。
   * 以前は `slaCreditBp()` の既定階梯を全組織へ一律に当てていたので、
   * SLA を約束していないプランにも「稼働率未達につき 30% 返金」が
   * 自動起票され、承認すればそのまま請求から引かれる状態だった。
   *
   * 上限ではないが `limits` に置くのは、`subscriptions.limit_overrides` で
   * **契約ごとに個別 SLA を上書きできる**ようにするため（個別見積りの
   * エンタープライズ契約で 99.95% を約束する、といった運用が実際にある）。
   *
   * `.default()` は `contentAssetBytes` と同じ理由（この列が入る前に
   * `plans` へ書かれた既存行を `parse()` が落とさないようにする）。
   */
  slaUptimeBp: z.number().int().min(0).max(10000).default(0),
  /**
   * Webメールの AI 機能で 1 か月に使えるトークン数（入力 + 出力の合計、**0 = 無制限**）。
   *
   * 容量や通数と違って AI は**使われるほど運営に原価が出る**（外部 API の従量課金）。
   * だから上限が要る。上限に達したときの挙動は組織ポリシーの `onLimit` で
   * 「止める（既定）」か「従量課金する」を契約者が選ぶ。
   *
   * 契約者が自分の接続先と鍵を使うプラン（`aiCustomEndpoint`）では原価が
   * 契約者側に移るので、ここは 0（無制限）にしてよい。
   *
   * `.default()` は `contentAssetBytes` と同じ理由（既存行の互換）。
   */
  aiMonthlyTokens: z.number().int().nonnegative().default(0),
  /**
   * プライベートメールアドレス（渡す相手ごとの使い捨て転送アドレス）の発行上限。
   *
   * **この項目だけ `0` の意味が他と違う。** 他の上限は 0 = 無制限だが、
   * ここは **0 = その機能を使えない**。`planFeaturesSchema` にフラグを足して
   * 「使えるか」と「何個まで」を 2 か所で表すと、必ず片方だけ更新されて
   * 「フラグは true なのに上限 0 で 1 個も作れない」という状態が生まれる。
   * 上限 1 本で両方を表す（`isPrivateAddressEnabled()` が唯一の判定）。
   *
   * **エンタープライズでも無制限にしない。** 使い捨てアドレスは「作って捨てる」
   * ものなので、無制限にすると発行速度の異常（＝乗っ取られた API トークンが
   * 名前空間を汚染している）を上限で検知できなくなる。
   *
   * `.default()` は `contentAssetBytes` と同じ理由（この列が入る前に `plans` へ
   * 書かれた既存行を `parse()` が落とさないようにする）。
   */
  privateAddressesMax: z.number().int().nonnegative().default(0),
  /**
   * 一斉配信（配信 CMS）の **1 日あたり / 1 か月あたりの通数上限**（0 = 無制限）。
   *
   * ここが無かった間、一斉配信の通数には**プラン上限も課金メータも一切
   * かかっていなかった**。唯一の流量制限だった `sending_streams.daily_cap` は
   * 契約者自身が設定する値で既定 0（無制限）なので、最安プランの契約者が
   * そのまま何百万通でも流せる状態だった（原価は運営の IP 評判で払う）。
   *
   * **0 は「そのプランでは使えない」ではなく「無制限」**にしてある。
   * `.default(0)` を付けている以上、この列が入る前に `plans` へ書かれた
   * 既存行は `parse()` で 0 になる。0 を「使わせない」にすると、
   * 起動時の再 upsert より前にこの値を読む経路で**既存の全組織の一斉配信が
   * 黙って止まる**。他の上限（`contentAssetBytes` 等）と意味を揃えるほうが、
   * 次に触る人が取り違えない。使わせないプランは 1 通のような小さい値ではなく
   * `planFeaturesSchema` 側にフラグを足して表すこと。
   *
   * 数え方は **JST の暦日 / 暦月**、単位は「注入した宛先数」
   * （`campaign_batches.recipient_count` の合計）。強制点は 2 つ:
   *   ① `validateCampaign()` / `preflight()` … 人が直せる段階で落とす
   *   ② `leaseBulkBatches()` … バッチ境界の backstop（上限を 1 バッチぶん
   *      超えうるが、10 万行を宛先 1 件ごとに数える代償を払わない）
   */
  bulkDailySends: z.number().int().nonnegative().default(0),
  bulkMonthlySends: z.number().int().nonnegative().default(0),
});
export type PlanLimits = z.infer<typeof planLimitsSchema>;

export const planFeaturesSchema = z.object({
  /** aurora-mail テナントを払い出して Webメールを提供するか */
  webmail: z.boolean(),
  catchAll: z.boolean(),
  /** ManageSieve(4190) を公開するか */
  sieveManage: z.boolean(),
  autoresponder: z.boolean(),
  /** ClamAV によるウイルススキャン */
  antivirus: z.boolean(),
  /**
   * メールボックスのクォータに 0（無制限）を指定できるか（§4.7）。
   * `resolveQuotaBytes()` がこのフラグで 0 の可否を判定する。
   */
  allowUnlimitedQuota: z.boolean(),
  dkimEd25519: z.boolean(),
  dedicatedIp: z.boolean(),
  /** mail.<顧客ドメイン> 名義の ACME 証明書を発行するか */
  customMailHostname: z.boolean(),
  apiAccess: z.boolean(),
  auditExport: z.boolean(),
  prioritySupport: z.boolean(),
  /**
   * 配信コンテンツを **HTML 直書き（`raw-html-v1`）** で保存できるか。
   *
   * 既定は false。ブロック編集（`blocks-v1`）なら差し込み・法定フッタ・
   * リンク抽出をこちらで構造的に保証できるが、生 HTML を許すと
   * 「法定フッタが実体として入っているか」を文字列検査に頼ることになる。
   * 検査を通せる運用体制がある契約だけに開ける機能として扱う。
   *
   * `.default(false)` は `contentAssetBytes` と同じ理由（既存行の互換）。
   */
  contentHtmlSource: z.boolean().default(false),

  /* ---------------- 受信者のプライバシー保護（`privacy.ts`） ---------------- */

  /**
   * Webメールのリモート画像を**こちらが代理で取得**する（画像プロキシ）。
   *
   * **全プランで true にする。** 受信者の IP と開封時刻を送信者から隠すのは
   * 契約者が払う金額の話ではなく、**受信者（多くは契約者の顧客）の保護**である。
   * ここを有料機能にすると「安いプランの利用者は追跡される」設計になり、
   * プライバシーを売り物にした瞬間に商品としての筋が悪くなる。
   * 上位プランで売るのは下の 2 つ（設備の量に比例するもの）。
   *
   * false にすると `resolvePrivacyPolicy()` が `proxy` を選べなくし、
   * **`direct` ではなく `block` に落とす**（プランの都合で保護が外れない）。
   */
  imageProxy: z.boolean().default(true),
  /**
   * プロキシの出口 IP をローテーションする。
   *
   * 固定 1 本だと「このサービスの利用者が開いた」ことは送信者に分かるし、
   * 取得の間隔から同一受信者を推測できる。複数 IP を保有・維持する原価が
   * かかるので、ここは上位プランの機能にする。
   */
  imageProxyIpRotation: z.boolean().default(false),
  /**
   * 受信の時点で画像を先読みし、**開封時刻そのものを隠す**。
   *
   * 開かないメールの画像まで取得するので帯域とキャッシュ容量を食う。
   * 原価が使用量に比例するため上位プラン向け。
   */
  imageProxyPrefetch: z.boolean().default(false),

  /* -------------------- Webメールの AI 機能（`ai-policy.ts`） -------------------- */

  /**
   * AI 機能（要約・返信提案・自然文検索・分類）を提供するか。
   * false のときは契約者パネルにも項目を出さない。
   */
  aiAssistant: z.boolean().default(false),
  /**
   * **接続先を契約者自身が指定できるか**（`openai_compat` / `ollama` +
   * 自分の API キー）。最上位プランの中心的な価値。
   *
   * 「社内のメール本文を外部の LLM に出せない」という要件は実在し、
   * 自社エンドポイントを指せることがそのまま導入の条件になる。
   * 同時に**運営側の推論原価が消える**（鍵が契約者のものになる）ので、
   * 上限緩和で売るより収益構造が素直になる。
   */
  aiCustomEndpoint: z.boolean().default(false),
});
export type PlanFeatures = z.infer<typeof planFeaturesSchema>;

/** 超過分の従量課金（単位: 銭ではなく「最小通貨単位」= JPY なら円） */
export const planOverageSchema = z.object({
  perMailboxCents: z.number().int().nonnegative(),
  perGiBCents: z.number().int().nonnegative(),
  /**
   * **必ず 0。送信通数は超過課金しない**（REQUIREMENTS.md §8.2）。
   *
   * 上限を超えた送信はノード側が `450 4.7.1 Daily sending limit exceeded` で
   * その日だけ絞る。金額に変換しない。理由は、アカウントを乗っ取られて
   * 10 万通送られた契約者に**こちらの検知が遅れたぶんの請求書を送りつける**
   * ことになるため。事故の損害額を青天井にしない、という設計判断。
   *
   * 列そのものは、この単価で発行済みの過去の請求書を表示するために残してある。
   * `generateInvoice` に生成経路は無い（`billing-outbound.test.ts` が固定）。
   */
  perThousandOutboundCents: z.number().int().nonnegative(),
  /**
   * AI トークン 100 万（入力 + 出力の合計）あたりの超過単価。
   *
   * **送信通数（`perThousandOutboundCents`）と違って、こちらは 0 以外を許す。**
   * 理由は事故の形が違うこと。送信通数の超過課金を禁じたのは「乗っ取られて
   * 10 万通送られた契約者に、こちらの検知遅れ分を請求してしまう」ためだが、
   * AI は**契約者自身の操作でしか動かない**（受信メールが勝手に推論を起こさない）。
   * さらに上限に達したときの既定挙動が `block`（止める）なので、
   * 課金され続けるのは契約者が明示的に `meter` を選んだ場合だけになる。
   *
   * 100 万トークン単位にしているのは、1 通の要約が数千トークンで、
   * 1 通単位や 1 千単位だと単価が 1 円未満になって丸めが効かないため。
   *
   * `.default(0)` は既存の `plans` 行を `parse()` が落とさないようにするため。
   */
  perMillionAiTokensCents: z.number().int().nonnegative().default(0),
});
export type PlanOverage = z.infer<typeof planOverageSchema>;

export const planDefinitionSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  description: z.string(),
  /** 月額（最小通貨単位）。0 = 無料 */
  monthlyPrice: z.number().int().nonnegative(),
  /** 年額。0 = 年払いなし */
  yearlyPrice: z.number().int().nonnegative(),
  currency: z.string().length(3),
  sortOrder: z.number().int(),
  isPublic: z.boolean(),
  limits: planLimitsSchema,
  features: planFeaturesSchema,
  overage: planOverageSchema,
});
export type PlanDefinition = z.infer<typeof planDefinitionSchema>;

const GiB = 1024 ** 3;
const MiB = 1024 ** 2;

/**
 * 標準プランカタログ。
 * さくらのレンタルサーバー等と同様、下位プランでも「まともに使える」容量を
 * 与えつつ、送信レートは上位ほど緩める（スパム踏み台化のリスク管理）。
 */
export const PLAN_CATALOG: readonly PlanDefinition[] = [
  {
    code: 'light',
    name: 'ライト',
    description: '個人・小規模向け。独自ドメイン1つ、メールアドレス10個まで。',
    monthlyPrice: 500,
    yearlyPrice: 5000,
    currency: 'JPY',
    sortOrder: 10,
    isPublic: true,
    limits: {
      domainsMax: 1,
      mailboxesMax: 10,
      aliasesMax: 20,
      forwardsPerMailboxMax: 3,
      storageBytesTotal: 20 * GiB,
      mailboxQuotaBytesDefault: 2 * GiB,
      mailboxQuotaBytesMax: 5 * GiB,
      messageSizeBytesMax: 25 * MiB,
      sendPerHourMax: 100,
      sendPerDayMax: 500,
      recipientsPerMessageMax: 50,
      imapConnectionsPerUserMax: 10,
      retentionDaysTrash: 30,
      // ライトは配信 CMS の実運用を想定しない。枠だけ最小で開けておく。
      contentAssetBytes: 1 * GiB,
      signupFormsMax: 1,
      // ライトは SLA の対象外（クレジットを自動起票しない）。掲示側の
      // 「サービスクレジットの対象プラン」も同じ値から生成する。
      slaUptimeBp: 0,
      aiMonthlyTokens: 0,
      // ライトでは提供しない（0 = 機能なし）。上位プランへの導線にする。
      privateAddressesMax: 0,
      // 一斉配信は「小さく試せる」枠に留める。ここを 0（無制限）にすると
      // 最安プランで IP 評判をいくらでも削れてしまう。
      bulkDailySends: 1_000,
      bulkMonthlySends: 5_000,
    },
    features: {
      webmail: true,
      catchAll: false,
      sieveManage: false,
      autoresponder: true,
      antivirus: true,
      allowUnlimitedQuota: false,
      dkimEd25519: false,
      dedicatedIp: false,
      customMailHostname: false,
      apiAccess: false,
      auditExport: false,
      prioritySupport: false,
      contentHtmlSource: false,
      /* 受信者のプライバシー保護。プロキシ自体は全プラン。上位で IP ローテとプリフェッチ。 */
      imageProxy: true,
      imageProxyIpRotation: false,
      imageProxyPrefetch: false,
      /* Webメールの AI。接続先の指定は最上位のみ（原価が契約者側へ移る）。 */
      aiAssistant: false,
      aiCustomEndpoint: false,
    },
    overage: { perMailboxCents: 100, perGiBCents: 30, perThousandOutboundCents: 0, perMillionAiTokensCents: 0 },
  },
  {
    code: 'standard',
    name: 'スタンダード',
    description: '中小企業向け。独自ドメイン5つ、メールアドレス100個まで。',
    monthlyPrice: 1500,
    yearlyPrice: 15000,
    currency: 'JPY',
    sortOrder: 20,
    isPublic: true,
    limits: {
      domainsMax: 5,
      mailboxesMax: 100,
      aliasesMax: 300,
      forwardsPerMailboxMax: 10,
      storageBytesTotal: 200 * GiB,
      mailboxQuotaBytesDefault: 5 * GiB,
      mailboxQuotaBytesMax: 30 * GiB,
      messageSizeBytesMax: 50 * MiB,
      sendPerHourMax: 500,
      sendPerDayMax: 3000,
      recipientsPerMessageMax: 100,
      imapConnectionsPerUserMax: 20,
      retentionDaysTrash: 60,
      contentAssetBytes: 10 * GiB,
      signupFormsMax: 5,
      slaUptimeBp: SLA_UPTIME_GUARANTEE_BP,
      aiMonthlyTokens: 0,
      privateAddressesMax: 10,
      bulkDailySends: 10_000,
      bulkMonthlySends: 100_000,
    },
    features: {
      webmail: true,
      catchAll: true,
      sieveManage: true,
      autoresponder: true,
      antivirus: true,
      allowUnlimitedQuota: false,
      dkimEd25519: true,
      dedicatedIp: false,
      customMailHostname: true,
      apiAccess: true,
      auditExport: false,
      prioritySupport: false,
      // 生 HTML はビジネス以上。法定表記の検査を文字列に頼る経路なので運用体制を要求する。
      contentHtmlSource: false,
      /* 受信者のプライバシー保護。プロキシ自体は全プラン。上位で IP ローテとプリフェッチ。 */
      imageProxy: true,
      imageProxyIpRotation: true,
      imageProxyPrefetch: false,
      /* Webメールの AI。接続先の指定は最上位のみ（原価が契約者側へ移る）。 */
      aiAssistant: false,
      aiCustomEndpoint: false,
    },
    overage: { perMailboxCents: 80, perGiBCents: 25, perThousandOutboundCents: 0, perMillionAiTokensCents: 0 },
  },
  {
    code: 'business',
    name: 'ビジネス',
    description: '中堅企業向け。ドメイン20、メールアドレス500、監査ログ出力に対応。',
    monthlyPrice: 5000,
    yearlyPrice: 50000,
    currency: 'JPY',
    sortOrder: 30,
    isPublic: true,
    limits: {
      domainsMax: 20,
      mailboxesMax: 500,
      aliasesMax: 2000,
      forwardsPerMailboxMax: 20,
      storageBytesTotal: 1000 * GiB,
      mailboxQuotaBytesDefault: 10 * GiB,
      mailboxQuotaBytesMax: 100 * GiB,
      messageSizeBytesMax: 100 * MiB,
      sendPerHourMax: 2000,
      sendPerDayMax: 20000,
      recipientsPerMessageMax: 200,
      imapConnectionsPerUserMax: 40,
      retentionDaysTrash: 90,
      contentAssetBytes: 50 * GiB,
      signupFormsMax: 30,
      slaUptimeBp: SLA_UPTIME_GUARANTEE_BP,
      aiMonthlyTokens: 2_000_000,
      privateAddressesMax: 100,
      bulkDailySends: 50_000,
      bulkMonthlySends: 500_000,
    },
    features: {
      webmail: true,
      catchAll: true,
      sieveManage: true,
      autoresponder: true,
      antivirus: true,
      allowUnlimitedQuota: false,
      dkimEd25519: true,
      dedicatedIp: false,
      customMailHostname: true,
      apiAccess: true,
      auditExport: true,
      prioritySupport: true,
      contentHtmlSource: true,
      /* 受信者のプライバシー保護。プロキシ自体は全プラン。上位で IP ローテとプリフェッチ。 */
      imageProxy: true,
      imageProxyIpRotation: true,
      imageProxyPrefetch: true,
      /* Webメールの AI。接続先の指定は最上位のみ（原価が契約者側へ移る）。 */
      aiAssistant: true,
      aiCustomEndpoint: false,
    },
    overage: { perMailboxCents: 60, perGiBCents: 20, perThousandOutboundCents: 0, perMillionAiTokensCents: 800 },
  },
  {
    code: 'enterprise',
    name: 'エンタープライズ',
    description: '大規模向け。ドメイン・メールアドレスは無制限、専用IP・優先サポート付き。',
    /*
     * **金額と容量を 0（＝無料・無制限）にしない**（R19）。
     *
     * 以前この行は `monthlyPrice: 0` で `storageBytesTotal` も
     * `contentAssetBytes` も 0（= 無制限）だった。0 は `exceedsLimit()` が
     * 常に false を返す値なので、**このプランを引いた組織は上限チェックが
     * 全て素通りし、しかも基本料 0 円・超過単価 0 円**で請求書が立たない。
     * 1 組織がノードの容量を食い潰しても、止める門も回収する経路も無い。
     *
     * REQUIREMENTS.md §8.1 の 4 段目（`dedicated`）が定額 30,000 円・
     * 容量 2,000 GiB・超過 15 円/GiB・送信 100,000 通/日 と書いているので、
     * **その実体をこの行に入れる**。`code` は `dedicated` へ改名しない
     * （`plans.code` は「デプロイ後の改名禁止」で、改名すると `seedPlans` が
     * 旧 code を retired にして既存契約が retired なプランを指す）。
     * ドメイン数・メールボックス数の無制限は要件表どおり 0 のまま。
     */
    monthlyPrice: 30000,
    yearlyPrice: 300000,
    currency: 'JPY',
    sortOrder: 40,
    isPublic: false,
    limits: {
      // 0 = 無制限（要件表の「無制限」）。
      domainsMax: 0,
      mailboxesMax: 0,
      aliasesMax: 0,
      forwardsPerMailboxMax: 50,
      // **無制限にしない。** 個別見積りで増やすときは
      // `subscriptions.limit_overrides` で契約ごとに上書きする。
      storageBytesTotal: 2000 * GiB,
      mailboxQuotaBytesDefault: 50 * GiB,
      mailboxQuotaBytesMax: 1024 * GiB,
      messageSizeBytesMax: 150 * MiB,
      sendPerHourMax: 10000,
      sendPerDayMax: 100000,
      recipientsPerMessageMax: 500,
      imapConnectionsPerUserMax: 100,
      retentionDaysTrash: 365,
      // 配信アセットも無制限にしない（S3 の費用が青天井になる）。
      contentAssetBytes: 200 * GiB,
      // 申込フォームの無制限は list-bombing の被害面積が読めない。
      signupFormsMax: 100,
      // 個別見積り。契約ごとの上書きは subscriptions.limit_overrides で行う。
      slaUptimeBp: SLA_UPTIME_GUARANTEE_BP,
      aiMonthlyTokens: 0,
      // **無制限にしない。** 発行速度の異常を上限で検知できなくなる。
      privateAddressesMax: 1000,
      // 個別交渉が前提のプラン。必要なら `subscriptions.limit_overrides` で
      // 契約ごとに絞る（プランを増やさずに表現する）。
      bulkDailySends: 0,
      bulkMonthlySends: 0,
    },
    features: {
      webmail: true,
      catchAll: true,
      sieveManage: true,
      autoresponder: true,
      antivirus: true,
      allowUnlimitedQuota: true,
      dkimEd25519: true,
      dedicatedIp: true,
      customMailHostname: true,
      apiAccess: true,
      auditExport: true,
      prioritySupport: true,
      contentHtmlSource: true,
      /* 受信者のプライバシー保護。プロキシ自体は全プラン。上位で IP ローテとプリフェッチ。 */
      imageProxy: true,
      imageProxyIpRotation: true,
      imageProxyPrefetch: true,
      /* Webメールの AI。接続先の指定は最上位のみ（原価が契約者側へ移る）。 */
      aiAssistant: true,
      aiCustomEndpoint: true,
    },
    // メールボックス数は無制限なので超過が定義できない（単価 0 = 課金しない）。
    // 容量だけは有限なので、要件表どおり 15 円/GiB を取る。
    overage: { perMailboxCents: 0, perGiBCents: 15, perThousandOutboundCents: 0, perMillionAiTokensCents: 0 },
  },
] as const;

export function findPlan(code: string): PlanDefinition | undefined {
  return PLAN_CATALOG.find((p) => p.code === code);
}

/** 0 を「無制限」として扱う上限チェック。超えていれば true。 */
export function exceedsLimit(current: number, max: number): boolean {
  if (max === 0) return false;
  return current > max;
}

/** 残枠。無制限なら null。 */
export function remainingQuota(current: number, max: number): number | null {
  if (max === 0) return null;
  return Math.max(0, max - current);
}

/* --------------------------------------------------- 一斉配信の通数上限 */

/** 一斉配信の通数上限に対する実績（JST の暦日 / 暦月）。 */
export interface BulkSendUsage {
  /** 当日（JST）に注入した宛先数。 */
  daily: number;
  /** 当月（JST）に注入した宛先数。 */
  monthly: number;
}

/** 上限に当たった側の内訳。当たっていなければ `null`。 */
export interface BulkQuotaBlock {
  scope: 'daily' | 'monthly';
  current: number;
  max: number;
  /** これから積む通数を含めても収まる残枠。 */
  remaining: number;
}

/**
 * 一斉配信の通数上限の判定。**この関数だけが定義**（3 か所の強制点が使う）。
 *
 * `adding` は「これから積む通数」。**0 でも 1 通ぶんは要求として数える**
 * （`Math.max(1, adding)`）。0 のときに「ちょうど上限に達している」を
 * 素通りさせると、払い出しの backstop が上限に達した瞬間から
 * 1 バッチずつ無限に超過を許すことになる。
 *
 * `scopes` を絞れるようにしてあるのは、**送信前チェックで日次を見ないため**。
 * 日次上限は払い出しが翌日へ自然に持ち越すので、「今日の枠に入りきらない」
 * ことを理由にキャンペーンごと落とすのは誤り（10 万通のキャンペーンが
 * 日次 1 万通のプランで永久に開始できなくなる）。月次は持ち越せないので
 * 人が直せる段階で落とす。
 *
 * 日次を先に見るのは、両方に当たっているときに「明日また試せる」で済む
 * 可能性を先に伝えるため。`max === 0` は無制限。
 */
export function bulkQuotaBlock(
  limits: Pick<PlanLimits, 'bulkDailySends' | 'bulkMonthlySends'>,
  usage: BulkSendUsage,
  adding = 0,
  scopes: readonly ('daily' | 'monthly')[] = ['daily', 'monthly'],
): BulkQuotaBlock | null {
  const want = Math.max(1, Math.trunc(adding));
  const checks: Array<{ scope: 'daily' | 'monthly'; current: number; max: number }> = [
    { scope: 'daily', current: usage.daily, max: limits.bulkDailySends },
    { scope: 'monthly', current: usage.monthly, max: limits.bulkMonthlySends },
  ];
  for (const c of checks) {
    if (c.max === 0 || !scopes.includes(c.scope)) continue;
    if (c.current + want > c.max) {
      return { scope: c.scope, current: c.current, max: c.max, remaining: Math.max(0, c.max - c.current) };
    }
  }
  return null;
}

/* --------------------------------------------------- 超過（従量）単価の掲示 */

export interface OveragePriceRow {
  readonly code: string;
  readonly name: string;
  /** 上限を超えたメールボックス 1 件あたり（税抜 / 税込）。0 なら課金しない。 */
  readonly perMailbox: { exclTax: number; inclTax: number };
  /** 上限を超えた容量 1 GiB あたり（税抜 / 税込）。0 なら課金しない。 */
  readonly perGiB: { exclTax: number; inclTax: number };
  /**
   * 上限を超えた AI トークン 100 万件あたり（税抜 / 税込）。0 なら課金しない。
   *
   * **単価だけ掲示して請求しない状態を作らない**（0061）。business は
   * `perMillionAiTokensCents = 800` を持っているのに請求側に生成経路が無く、
   * 「超過ぶんは今月の請求に加算されます」という通知だけが飛んでいた。
   */
  readonly perMillionAiTokens: { exclTax: number; inclTax: number };
}

/**
 * 公開プランの超過（従量）単価。**特商法 11 条の「販売価格」に載せる実額**。
 *
 * 上限超過で従量課金が発生するのに単価がどこにも掲示されていない状態は、
 * 販売価格の表示として不完全（A267）。掲示・料金ページ・`GET /public/plans`
 * が同じ配列から出るようにして、片方だけ古くなる余地を消す。
 *
 * **送信通数は含めない。** 送信の超過はノード側が 450 で絞るだけで
 * 金額に変換しない（REQUIREMENTS.md §8.2）。掲示に書くと、実装が
 * 課金しない料金を掲示していることになる。
 */
export function overagePriceRows(
  taxRateBp: number = DEFAULT_TAX_RATE_BP,
  plans: readonly PlanDefinition[] = PLAN_CATALOG,
): OveragePriceRow[] {
  return plans
    .filter((p) => p.isPublic)
    .map((p) => ({
      code: p.code,
      name: p.name,
      perMailbox: {
        exclTax: p.overage.perMailboxCents,
        inclTax: taxIncludedAmount(p.overage.perMailboxCents, taxRateBp),
      },
      perGiB: {
        exclTax: p.overage.perGiBCents,
        inclTax: taxIncludedAmount(p.overage.perGiBCents, taxRateBp),
      },
      perMillionAiTokens: {
        exclTax: p.overage.perMillionAiTokensCents,
        inclTax: taxIncludedAmount(p.overage.perMillionAiTokensCents, taxRateBp),
      },
    }));
}

/** 掲示用の 1 行（「スタンダード: メールボックス 88 円/件・容量 27 円/GiB（税込）」）。 */
export function overagePriceLine(row: OveragePriceRow): string {
  const parts: string[] = [];
  if (row.perMailbox.exclTax > 0) {
    parts.push(`メールボックス ${row.perMailbox.inclTax.toLocaleString('ja-JP')} 円/件`);
  }
  if (row.perGiB.exclTax > 0) {
    parts.push(`容量 ${row.perGiB.inclTax.toLocaleString('ja-JP')} 円/GiB`);
  }
  if (row.perMillionAiTokens.exclTax > 0) {
    // AI は「上限に達したら止める」が既定。従量課金は契約者が選んだときだけ
    // 走るので、掲示にもその条件を書く（掲示と実装を同じ条件にする）。
    parts.push(
      `AI ${row.perMillionAiTokens.inclTax.toLocaleString('ja-JP')} 円/100 万トークン（「従量課金で継続」を選んだ場合のみ）`,
    );
  }
  return parts.length > 0 ? `${row.name}: ${parts.join('・')}（税込・月額）` : `${row.name}: 超過課金なし`;
}
